/**
 * pv-a11y-overlays.js — go-live a11y enhancements for theme overlays.
 * s285 (A1 cart drawer) → s286 generalized to a reusable factory + open-stack.
 * Self-contained, dependency-free, idempotent.
 *
 * Every Ella overlay opens/closes via a body-class toggle (theme.js / vendor JS)
 * with NO focus handling and NO role=dialog injection (verified: driving JS never
 * references role/aria-modal). role="dialog" + aria-modal="true" (added in the
 * overlay markup) are only honest if focus is actually moved in, trapped while
 * open, and restored on close. This wires that up by observing the body class —
 * no edits to the theme's jQuery/vendor open paths.
 *
 * Overlays can STACK (e.g. add-to-cart from inside quick-view opens the cart
 * drawer while quick-view is still open — theme.js). A single shared keydown
 * listener acts on the TOPMOST open overlay only, so one Escape closes one
 * overlay (not all), and focus restores down the stack correctly.
 *
 * Registered overlays (rootId / body-class trigger / close selector):
 *   A1 cart drawer    #halo-cart-sidebar              cart-sidebar-show       [data-close-cart-sidebar]
 *   A2 quick-view     #halo-quick-view-popup          quick-view-show         [data-close-quick-view-popup]
 *   A3 quick-shop     #halo-quickshop-popup-option-3  quickshop-popup-show    [data-close-quick-shop-popup]
 *   A4 login sidebar  #halo-auth-sidebar              auth-sidebar-show       [data-close-auth-sidebar]
 */
(function () {
  'use strict';
  if (window.__pvA11yOverlays) return;
  window.__pvA11yOverlays = true;

  var FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function visibleFocusable(root) {
    return Array.prototype.slice.call(root.querySelectorAll(FOCUSABLE)).filter(function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;
    });
  }

  var overlays = [];
  var openStack = []; // overlays currently open, in open order; last item = topmost

  function makeOverlay(rootId, bodyClass, closeSelector) {
    var root = document.getElementById(rootId);
    if (!root) return; // overlay not rendered under current theme settings/template — skip
    overlays.push({
      root: root,
      bodyClass: bodyClass,
      closeSelector: closeSelector,
      lastFocus: null,
      openTimer: null,
      isOpen: document.body.classList.contains(bodyClass)
    });
  }

  function stackRemove(o) {
    var i = openStack.indexOf(o);
    if (i !== -1) openStack.splice(i, 1);
  }

  function openOverlay(o) {
    o.openTimer = null;
    // stale-timeout guard: if the overlay was closed again before this deferred
    // callback fired, do nothing (no focus move, no stack push).
    if (!document.body.classList.contains(o.bodyClass)) return;
    if (openStack.indexOf(o) === -1) openStack.push(o);
    var f = visibleFocusable(o.root);
    if (f.length) {
      f[0].focus();
    } else {
      // fallback: make the dialog root programmatically focusable and focus it
      o.root.setAttribute('tabindex', '-1');
      o.root.focus();
    }
  }

  function closeOverlay(o) {
    if (o.openTimer) { window.clearTimeout(o.openTimer); o.openTimer = null; }
    var wasTop = openStack.length > 0 && openStack[openStack.length - 1] === o;
    stackRemove(o);
    // Only move focus if this overlay was the topmost (or nothing remains open),
    // so closing a still-covered overlay never steals focus from the one above it.
    // Restoring lastFocus lands focus on the trigger — which for a stacked overlay
    // sits inside the overlay beneath it, i.e. exactly where focus should return.
    if (wasTop || openStack.length === 0) {
      if (o.lastFocus && typeof o.lastFocus.focus === 'function') {
        try { o.lastFocus.focus(); } catch (err) {}
      }
    }
    o.lastFocus = null;
  }

  function onKeydown(e) {
    if (!openStack.length) return;
    var o = openStack[openStack.length - 1]; // act on TOPMOST overlay only
    if (e.key === 'Escape' || e.key === 'Esc') {
      var closeBtn = o.root.querySelector(o.closeSelector);
      if (closeBtn) { e.preventDefault(); closeBtn.click(); }
      return;
    }
    if (e.key !== 'Tab') return;
    var f = visibleFocusable(o.root);
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    else if (!o.root.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
  }

  makeOverlay('halo-cart-sidebar', 'cart-sidebar-show', '[data-close-cart-sidebar]');
  makeOverlay('halo-quick-view-popup', 'quick-view-show', '[data-close-quick-view-popup]');
  makeOverlay('halo-quickshop-popup-option-3', 'quickshop-popup-show', '[data-close-quick-shop-popup]');
  makeOverlay('halo-auth-sidebar', 'auth-sidebar-show', '[data-close-auth-sidebar]');

  if (!overlays.length) return;

  // one permanent capture-phase listener; it reads openStack at event time.
  document.addEventListener('keydown', onKeydown, true);

  new MutationObserver(function () {
    for (var i = 0; i < overlays.length; i++) {
      var o = overlays[i];
      var nowOpen = document.body.classList.contains(o.bodyClass);
      if (nowOpen && !o.isOpen) {
        o.isOpen = true;
        // save the trigger (e.g. cart icon / in-overlay button) synchronously,
        // before the open delay
        o.lastFocus = document.activeElement;
        // small delay lets the overlay finish its open transition before focusing
        o.openTimer = window.setTimeout(openOverlay.bind(null, o), 60);
      } else if (!nowOpen && o.isOpen) {
        o.isOpen = false;
        closeOverlay(o);
      }
    }
  }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
})();
